import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

export interface RouteSuggestion {
  path: string;
  similarity: number;
  displayName: string;
}

@Injectable({
  providedIn: 'root'
})
export class RouteMatcherService {

  constructor(private router: Router) { }

  /**
   * Calculate Levenshtein distance between two strings
   */
  private levenshteinDistance(str1: string, str2: string): number {
    const m = str1.length;
    const n = str2.length;
    const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 0; i <= m; i++) {
      dp[i][0] = i;
    }

    for (let j = 0; j <= n; j++) {
      dp[0][j] = j;
    }

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (str1[i - 1] === str2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = Math.min(
            dp[i - 1][j] + 1,    // deletion
            dp[i][j - 1] + 1,    // insertion
            dp[i - 1][j - 1] + 1 // substitution
          );
        }
      }
    }

    return dp[m][n];
  }

  /**
   * Calculate similarity percentage between two strings (0-100)
   */
  private calculateSimilarity(str1: string, str2: string): number {
    const lower1 = str1.toLowerCase();
    const lower2 = str2.toLowerCase();
    
    const distance = this.levenshteinDistance(lower1, lower2);
    const maxLength = Math.max(str1.length, str2.length);
    
    if (maxLength === 0) return 100;
    
    let similarity = ((1 - distance / maxLength) * 100);
    
    // Boost similarity for common prefixes
    const commonPrefixLength = this.getCommonPrefixLength(lower1, lower2);
    if (commonPrefixLength >= 5) {
      // Significant prefix match - boost similarity
      const prefixBoost = (commonPrefixLength / Math.max(lower1.length, lower2.length)) * 30;
      similarity = Math.min(100, similarity + prefixBoost);
    }
    
    // Boost if one is substring of the other
    if (lower1.includes(lower2) || lower2.includes(lower1)) {
      similarity = Math.max(similarity, 80);
    }
    
    // Check for word component matches (split by dash/slash)
    const words1 = lower1.split(/[-\/]/);
    const words2 = lower2.split(/[-\/]/);
    
    for (const word1 of words1) {
      for (const word2 of words2) {
        if (word1.length >= 4 && word2.length >= 4) {
          if (word1.startsWith(word2.substring(0, 4)) || word2.startsWith(word1.substring(0, 4))) {
            similarity = Math.max(similarity, 65);
          }
        }
      }
    }
    
    return similarity;
  }
  
  /**
   * Get the length of common prefix between two strings
   */
  private getCommonPrefixLength(str1: string, str2: string): number {
    let i = 0;
    const minLength = Math.min(str1.length, str2.length);
    
    while (i < minLength && str1[i] === str2[i]) {
      i++;
    }
    
    return i;
  }

  /**
   * Get all valid routes from the router configuration
   */
  private getAllRoutes(): string[] {
    const routes: string[] = [];
    
    const extractPaths = (configs: any[], parentPath: string = ''): void => {
      for (const config of configs) {
        if (config.path && config.path !== '**') {
          // Build full path
          const fullPath = parentPath ? `${parentPath}/${config.path}` : config.path;
          
          // Remove dynamic segments for comparison (e.g., :id, :name)
          const staticPath = fullPath.replace(/\/:[^/]+/g, '');
          
          if (staticPath && !routes.includes(staticPath)) {
            routes.push(staticPath);
          }
          
          // Also add the path with one segment for parameter routes
          const pathWithParams = fullPath.split('/').filter(segment => !segment.startsWith(':')).join('/');
          if (pathWithParams && pathWithParams !== staticPath && !routes.includes(pathWithParams)) {
            routes.push(pathWithParams);
          }
          
          // For routes with parameters, also store the base path
          // e.g., "oferta/:name/:id" -> store "oferta"
          const basePath = fullPath.split('/')[0].replace(/:[^/]+/g, '');
          if (basePath && !routes.includes(basePath)) {
            routes.push(basePath);
          }
        }
        
        // Recursively process child routes
        if (config.children) {
          extractPaths(config.children, config.path);
        }
      }
    };
    
    extractPaths(this.router.config);
    
    return routes;
  }
  
  /**
   * Clean and normalize URL for comparison
   */
  private normalizeUrl(url: string): string {
    return url
      .toLowerCase()
      .replace(/^\//, '') // Remove leading slash
      .split('?')[0] // Remove query params
      .replace(/,/g, '/') // Replace commas with slashes (common typo)
      .replace(/\s+/g, '-') // Replace spaces with dashes
      .replace(/[^\w\d\-\/]/g, ''); // Remove special characters except dash and slash
  }
  
  /**
   * Extract base path from URL (first segment)
   */
  private getBasePath(url: string): string {
    const normalized = this.normalizeUrl(url);
    return normalized.split('/')[0];
  }

  /**
   * Find similar routes to the requested URL
   * @param requestedUrl The URL that was not found
   * @param minSimilarity Minimum similarity threshold (0-100)
   * @param maxResults Maximum number of suggestions to return
   */
  findSimilarRoutes(
    requestedUrl: string, 
    minSimilarity: number = 60, 
    maxResults: number = 5
  ): RouteSuggestion[] {
    // Normalize the URL
    const normalizedUrl = this.normalizeUrl(requestedUrl);
    
    if (!normalizedUrl) {
      return [];
    }
    
    const allRoutes = this.getAllRoutes();
    const suggestions: RouteSuggestion[] = [];
    const basePath = this.getBasePath(normalizedUrl);
    
    // Extract first word from base path (before any dash)
    const baseWord = basePath.split('-')[0];
    
    for (const route of allRoutes) {
      let similarity = this.calculateSimilarity(normalizedUrl, route);
      
      // Boost similarity if base paths match or are very similar
      const routeBasePath = this.getBasePath(route);
      const basePathSimilarity = this.calculateSimilarity(basePath, routeBasePath);
      
      // If base paths are very similar, boost the overall similarity
      if (basePathSimilarity >= 70) {
        similarity = Math.max(similarity, basePathSimilarity);
      }
      
      // Also check if the base path is a substring or prefix match
      if (basePath.startsWith(routeBasePath) || routeBasePath.startsWith(basePath)) {
        similarity = Math.max(similarity, 75);
      }
      
      // Check if first word of base path matches or is similar to route
      // e.g., "oferta" from "oferta-urodziny" should match "oferty" or "oferta"
      const routeFirstWord = route.split(/[-\/]/)[0];
      if (baseWord.length >= 4 && routeFirstWord.length >= 4) {
        const wordSimilarity = this.calculateSimilarity(baseWord, routeFirstWord);
        if (wordSimilarity >= 70) {
          // Strong word match - boost similarity significantly
          similarity = Math.max(similarity, wordSimilarity);
        }
        
        // Special boost for very similar words (like oferta/oferty)
        const commonPrefix = this.getCommonPrefixLength(baseWord, routeFirstWord);
        if (commonPrefix >= Math.min(baseWord.length, routeFirstWord.length) - 1) {
          // Almost identical words (differ by 1 char or less)
          similarity = Math.max(similarity, 85);
        }
      }
      
      if (similarity >= minSimilarity) {
        // Avoid duplicates
        const existingSuggestion = suggestions.find(s => s.path === `/${route}`);
        if (!existingSuggestion || existingSuggestion.similarity < similarity) {
          if (existingSuggestion) {
            existingSuggestion.similarity = similarity;
          } else {
            suggestions.push({
              path: `/${route}`,
              similarity: similarity,
              displayName: this.formatRouteName(route)
            });
          }
        }
      }
    }
    
    // Sort by similarity (highest first) and limit results
    suggestions.sort((a, b) => b.similarity - a.similarity);
    
    return suggestions.slice(0, maxResults);
  }

  /**
   * Format route path for display
   */
  private formatRouteName(route: string): string {
    return route
      .split('/')
      .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
      .join(' / ');
  }

  /**
   * Get the best match route if similarity is high enough
   * @param requestedUrl The URL that was not found
   * @param autoRedirectThreshold Threshold for automatic redirect (default 85%)
   */
  getBestMatch(requestedUrl: string, autoRedirectThreshold: number = 85): string | null {
    const suggestions = this.findSimilarRoutes(requestedUrl, Math.min(autoRedirectThreshold, 70), 3);
    
    if (suggestions.length > 0 && suggestions[0].similarity >= autoRedirectThreshold) {
      return suggestions[0].path;
    }
    
    // Additional check: if base path matches exactly a route, redirect there
    const normalizedUrl = this.normalizeUrl(requestedUrl);
    const basePath = this.getBasePath(normalizedUrl);
    const baseWord = basePath.split('-')[0];
    const allRoutes = this.getAllRoutes();
    
    for (const route of allRoutes) {
      // Exact match on base path or first word
      if (route === basePath || route === baseWord) {
        return `/${route}`;
      }
      
      // Check if route starts with base word and is very similar
      const routeFirstWord = route.split(/[-\/]/)[0];
      if (baseWord.length >= 4 && routeFirstWord.length >= 4) {
        const commonPrefix = this.getCommonPrefixLength(baseWord.toLowerCase(), routeFirstWord.toLowerCase());
        // If they share most of their letters (like "oferta" and "oferty")
        if (commonPrefix >= Math.min(baseWord.length, routeFirstWord.length) - 1) {
          return `/${route}`;
        }
      }
    }
    
    return null;
  }
}
